import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import theme from "../../../assets/theme";

const ContractTable = ({ metaData, setModal }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      // ignore non-left mouse buttons
      if (e.button && e.button !== 0) return;

      // If ref is not mounted yet, do nothing
      if (!wrapperRef.current) return;

      // Use composedPath when available (better for SVG / shadow DOM)
      const path = e.composedPath ? e.composedPath() : e.path || [];

      const clickedInside = path.length
        ? path.includes(wrapperRef.current)
        : wrapperRef.current.contains(e.target);

      if (!clickedInside) {
        setModal({ state: false });
      }
    };

    // Capture phase is more reliable when inner elements call stopPropagation()
    document.addEventListener("click", handleOutsideClick, true);
    // also handle touch for mobile
    document.addEventListener("touchstart", handleOutsideClick, true);

    return () => {
      document.removeEventListener("click", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
    };
  }, [setModal]);

  const filteredData = metaData.filter(
    (item) =>
      String(item.contractNo || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(item.caseId || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <PageContainer>
      <ContentWrapper>
        <Card ref={wrapperRef}>
          <SearchBar>
            <SearchContainer>
              <SearchInputWrapper>
                <SearchIcon
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Search by contract number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchInputWrapper>
              <RecordCounter>
                <CalendarIcon
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </CalendarIcon>
                <CounterText>{sortedData.length} Records</CounterText>
              </RecordCounter>
            </SearchContainer>
          </SearchBar>

          <TableWrapper>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>
                    <SortButton onClick={() => handleSort("contractNo")}>
                      <IconWrapper>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </IconWrapper>
                      <span>Contract No</span>
                      <SortIcon
                        className="sort-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="m7 15 5 5 5-5" />
                        <path d="m7 9 5-5 5 5" />
                      </SortIcon>
                    </SortButton>
                  </TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {sortedData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <ContractCell>
                        <ContractBadge>
                          {item.contractNo.slice(-2)}
                        </ContractBadge>
                        <ContractNumber>{item.contractNo}</ContractNumber>
                      </ContractCell>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          {sortedData.length === 0 && (
            <EmptyState>
              <EmptyIconWrapper>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </EmptyIconWrapper>
              <EmptyTitle>No records found</EmptyTitle>
            </EmptyState>
          )}
        </Card>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ContractTable;

const PageContainer = styled.div`
  min-height: 70vh;
  padding: 24px;
`;

const ContentWrapper = styled.div`
  width: 30%;
  background-color: #ffffff;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 8px;
  transform: translate(-50%, -50%);
  margin: 0 auto;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const SearchBar = styled.div`
  background: linear-gradient(to right, #eff6ff, #eef2ff);
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchInputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const SearchIcon = styled.svg`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  width: 20px;
  height: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #3b82f6;
    border-color: transparent;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const RecordCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
`;

const CalendarIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #475569;
`;

const CounterText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #475569;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #eff6ff;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 16px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #293461;
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #475569;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #2563eb;
  }

  &:hover .sort-icon {
    opacity: 1;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const SortIcon = styled.svg`
  width: 16px;
  height: 16px;
  opacity: 0;
  transition: opacity 0.2s;
`;

const TableCell = styled.td`
  padding: 16px;
`;

const ContractCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ContractBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(to bottom right, rgba(237, 221, 83, 1), #00838c);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const ContractNumber = styled.span`
  font-weight: 600;
  color: #1e293b;
  transition: color 0.2s;

  ${TableRow}:hover & {
    color: #2563eb;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 16px;
`;

const EmptyIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;
